import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button, IconButton } from '@xing-com/button';
import { useLocalStorage } from '@xing-com/crate-hooks-use-web-storage';
import { MenuItem } from '@xing-com/crate-jobs-components-menu-item';
import {
  JOBS_REPORTED_JOBS,
  SEARCH_ALERTS_MODAL_TYPES,
} from '@xing-com/crate-jobs-constants';
import type { JobDetails } from '@xing-com/crate-jobs-helpers';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import { IconMore } from '@xing-com/icons';
import { Menu } from '@xing-com/menu';
import { usePopOver } from '@xing-com/pop-over';
import { space3XL } from '@xing-com/tokens';

import { useModal, useSearchAlertModal, useShareJob } from '../../../../hooks';

import { ReportJobModal } from './report-job-modal';

const StyledMenu = styled(Menu)`
  margin-top: ${space3XL};
  white-space: nowrap;
  transform: none !important; // We need to override the transform property to prevent the menu from being positioned incorrectly when it is opened.
`;

export type Action = 'REPORT_JOB' | 'SEARCH_ALERT' | 'SHARE_JOB' | 'MANAGE_JOB';

type Props = {
  job: JobDetails;
  iconOnly?: boolean;
};

export const MoreMenuButton: React.FC<Props> = ({ job, iconOnly = false }) => {
  const popOver = usePopOver();
  const { formatMessage } = useIntl();
  const trackNwt = useJobsNwtTracking();
  const { activeModal, toggleModal } = useModal();
  const { triggerShare } = useShareJob();
  const [reportedJobAds, setReportedJobAds] = useLocalStorage<string[]>(
    JOBS_REPORTED_JOBS,
    []
  );
  const { id: jobId, searchAlertFilters, redirectsToThirdPartyUrl } = job;
  const { openSearchAlertModal } = useSearchAlertModal({
    type: SEARCH_ALERTS_MODAL_TYPES.JOB_DETAILS,
    params: searchAlertFilters,
    isThirdPartyJob: redirectsToThirdPartyUrl,
  });
  const canCreateSearchAlert = false; // Temporarily disabled until we can create more meaningful alerts with CS - https://new-work.atlassian.net/browse/JSR-5214
  const isJobReported = reportedJobAds.includes(jobId);

  const MoreButton = iconOnly ? IconButton : Button;

  const reportJobAd = (jobId: string): void => {
    setReportedJobAds([...reportedJobAds, jobId]);
  };

  const handleOnPerformAction =
    (action: Action | 'REPORTED_JOB') =>
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
      event.preventDefault();
      event.stopPropagation();
      popOver.handleHide();
      if (action === 'SEARCH_ALERT') openSearchAlertModal();
      if (action === 'SHARE_JOB') triggerShare();
      if (action === 'REPORT_JOB') toggleModal('REPORT_JOB');
    };

  const handleOnMoreActionsClick = (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ): void => {
    event.preventDefault();
    event.stopPropagation();
    popOver.togglePopOver();
    if (!popOver.show) {
      trackNwt({
        event: 'clicked',
        eventSchema: 'basic',
        element: 'meatball_menu_open',
        page: redirectsToThirdPartyUrl ? 'jobs/detail_external' : 'jobs/detail',
        itemUrn: `surn:x-xing:jobs:posting:${jobId}`,
      });
    }
  };

  return (
    <>
      <MoreButton
        size={iconOnly ? 'large' : 'medium'}
        sizeConfined="medium"
        variant="secondary"
        icon={IconMore}
        ref={popOver.triggerRef}
        onClick={handleOnMoreActionsClick}
        aria-label={formatMessage({ id: 'JOBS_DETAIL_MENU_BUTTON_A11Y' })}
        data-testid="job-details-more-menu-button"
      />

      <StyledMenu
        isInFlow
        onOutsideClick={popOver.handleHide}
        show={popOver.show}
        triggerRef={popOver.triggerRef}
      >
        {canCreateSearchAlert ? (
          <MenuItem
            localeKey="JOBS_CREATE_SEARCH_ALERT"
            onClick={handleOnPerformAction('SEARCH_ALERT')}
          />
        ) : null}
        <MenuItem
          localeKey="JOBS_JOB_DETAILS_SHARE_JOB"
          onClick={handleOnPerformAction('SHARE_JOB')}
        />

        <MenuItem
          localeKey={`JOBS_JOB_DETAILS_${
            isJobReported ? 'REPORTED_JOB' : 'REPORT_JOB'
          }`}
          onClick={handleOnPerformAction(
            isJobReported ? 'REPORTED_JOB' : 'REPORT_JOB'
          )}
        />
      </StyledMenu>

      <ReportJobModal
        isShown={activeModal === 'REPORT_JOB'}
        toggle={toggleModal}
        jobId={jobId}
        reportJobAd={reportJobAd}
      />
    </>
  );
};
